import clsx from "clsx";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faRotate,
  faTriangleExclamation,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";

import {
  CC_ALERT_DANGER_BODY,
  CC_ALERT_DANGER_ITEM,
  CC_ALERT_DANGER_ITEM_BODY,
  CC_ALERT_DANGER_ITEM_TITLE,
  CC_ALERT_DANGER_PANEL,
  CC_ALERT_DANGER_TITLE,
} from "@/components/case-checking/caseCheckingChrome";
import type { ClassificationFailedDocument } from "@/components/case-checking/documentClassificationUi";
import { Button } from "@/components/ui/button/Button";

function failureHeading(count: number): string {
  return count === 1
    ? "One document could not be classified"
    : `${count} documents could not be classified`;
}

function FailureDetails({ failures }: { failures: ClassificationFailedDocument[] }) {
  return (
    <>
      <p className={CC_ALERT_DANGER_BODY}>
        The file check can continue, but these documents were skipped. Retry processing, or re-upload
        the file if it may be corrupted. Contact support if the error persists.
      </p>
      <ul className="mt-3 space-y-2">
        {failures.map((failure) => (
          <li key={failure.id ?? `${failure.name}:${failure.classificationError}`} className={CC_ALERT_DANGER_ITEM}>
            <p className={CC_ALERT_DANGER_ITEM_TITLE}>{failure.name}</p>
            <p className={CC_ALERT_DANGER_ITEM_BODY}>{failure.classificationError}</p>
          </li>
        ))}
      </ul>
    </>
  );
}

export function ClassificationFailedBanner({
  failures,
  className,
  compactTitle,
  collapsible = false,
  defaultExpanded = false,
  onRetryAll,
  isRetrying = false,
  retryDisabled = false,
  retryLabel,
}: {
  failures: ClassificationFailedDocument[];
  className?: string;
  /** Single-failure inline mode (document drawer): custom heading, no list chrome. */
  compactTitle?: string;
  /** Slim one-line alert; expand for file list and guidance. */
  collapsible?: boolean;
  defaultExpanded?: boolean;
  /** Callback to retry classification for the failed files. */
  onRetryAll?: () => void;
  /** True while the retry request is in flight. */
  isRetrying?: boolean;
  /** Disable the retry button (e.g. when processing is already running). */
  retryDisabled?: boolean;
  /** Override label for the retry button (defaults to "Retry all files"). */
  retryLabel?: string;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (failures.length === 0) {
    return null;
  }

  const effectiveRetryLabel =
    retryLabel ?? (compactTitle && failures.length === 1 ? "Retry classification" : "Retry all files");

  const retryButton = onRetryAll ? (
    <Button
      outline
      size="sm"
      type="button"
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onRetryAll();
      }}
      disabled={retryDisabled || isRetrying}
      loading={isRetrying}
      icon={<FontAwesomeIcon icon={faRotate} className="h-3.5 w-3.5" />}
      data-testid="classification-failed-retry-all"
    >
      {effectiveRetryLabel}
    </Button>
  ) : null;

  if (compactTitle && failures.length === 1) {
    const failure = failures[0];
    return (
      <div className={clsx(CC_ALERT_DANGER_PANEL, className)} role="alert" data-testid="classification-failed-banner">
        <div className="flex items-start justify-between gap-2">
          <p className={CC_ALERT_DANGER_TITLE}>{compactTitle}</p>
          {retryButton}
        </div>
        <p className={CC_ALERT_DANGER_BODY}>{failure.classificationError}</p>
      </div>
    );
  }

  const heading = failureHeading(failures.length);

  if (collapsible && !expanded) {
    return (
      <div
        role="alert"
        className={clsx(
          "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ring-1 ring-rose-500/25 bg-rose-500/5 text-rose-800 dark:text-rose-200",
          className,
        )}
      >
        <Button
          plain
          type="button"
          size="unstyled"
          onClick={() => setExpanded(true)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          data-testid="classification-failed-banner-collapsed"
        >
          <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" />
          <span className="min-w-0 flex-1 font-medium">{heading}</span>
          <span className="shrink-0 text-xs text-rose-700/80 dark:text-rose-300/80">Show details</span>
          <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3 shrink-0 opacity-70" />
        </Button>
        {retryButton}
      </div>
    );
  }

  return (
    <div className={clsx(CC_ALERT_DANGER_PANEL, className)} role="alert" data-testid="classification-failed-banner">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={CC_ALERT_DANGER_TITLE}>{heading}</p>
        <div className="flex items-center gap-2">
          {retryButton}
          {collapsible ? (
            <Button
              plain
              type="button"
              size="unstyled"
              className="shrink-0 text-xs text-rose-700/90 hover:text-rose-900 dark:text-rose-300 dark:hover:text-rose-100"
              onClick={() => setExpanded(false)}
              data-testid="classification-failed-banner-collapse"
            >
              Hide
              <FontAwesomeIcon icon={faChevronUp} className="ml-1 h-3 w-3" />
            </Button>
          ) : null}
        </div>
      </div>
      <FailureDetails failures={failures} />
    </div>
  );
}
